import React, { useEffect, useState, useRef } from "react";
import { Box, Typography } from "@mui/material";
import useAuth from "../../hooks/Auth/useAuth";
import axios from "axios";
import Swal from "sweetalert2";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const camerasList = [
  { camera_name: "left", show: true },
  { camera_name: "front", show: true },
  { camera_name: "right", show: true },
  { camera_name: "incabin", show: true },
];

const SOCKET_TIMEOUT = 6000;
const SPIN_PERIOD = 1200; // 1.2 s per revolution

export default function CamerasGrid({ cartId }) {
  const { relevantUrl, relevantSite, sitesSockectsConnection, auth } = useAuth();

  const [cameraImages, setCameraImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [spinner, setSpinner] = useState(0);

  const timeoutRef = useRef(null);
  const rafRef = useRef(null);

  /* ───────── spinner animation ───────── */
  useEffect(() => {
    if (!loading) {
      cancelAnimationFrame(rafRef.current);
      setSpinner(0);
      return;
    }
    const start = performance.now();
    const animate = (t) => {
      setSpinner((((t - start) % SPIN_PERIOD) / SPIN_PERIOD) * 100);
      rafRef.current = requestAnimationFrame(animate);
    };
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [loading]);

  /* ───────── socket handler ───────── */
  const handleSocketData = (data) => {
    console.log(
      "[CamerasGrid] cartCamerasData received",
      {
        cartId,
        site: relevantSite,
        payloadType: typeof data,
        length: typeof data === "string" ? data.length : undefined,
      }
    );
    const { cameras_data_list } = JSON.parse(data);
    const images =
      (cameras_data_list || [])
        .filter((c) => c.image_base64)
        .map((c) => ({
          name: c.camera_name,
          image: `data:image/jpeg;base64,${c.image_base64}`,
        }));

    clearTimeout(timeoutRef.current);
    setCameraImages(images);
    setLoading(false);

    console.log(
      "[CamerasGrid] parsed images",
      { count: images.length, names: images.map((i) => i.name) }
    );
  };

  /* ───────── subscribe / unsubscribe ───────── */
  useEffect(() => {
    if (!relevantSite || !sitesSockectsConnection?.[relevantSite]) return;
    const sock = sitesSockectsConnection[relevantSite];

    console.log("[CamerasGrid] subscribing to socket", {
      cartId,
      site: relevantSite,
      connected: !!sock?.connected,
      socketId: sock?.id,
    });

    sock.on("cartCamerasData", handleSocketData);
    timeoutRef.current = setTimeout(() => {
      console.warn(
        `[CamerasGrid] socket timeout after ${SOCKET_TIMEOUT}ms — no frames received`,
        { cartId, site: relevantSite }
      );
      setLoading(false);
    }, SOCKET_TIMEOUT);


    return () => {
      clearTimeout(timeoutRef.current);
      console.log("[CamerasGrid] cleanup: turning off cameras & unsubscribing", {
        cartId,
        site: relevantSite,
      });
      sock.emit("sm_turn_off_cameras_event", cartId);
      sock.off("cartCamerasData", handleSocketData);
    };
  }, [sitesSockectsConnection, relevantSite, cartId]);

  /* ───────── request images once ───────── */
  useEffect(() => {
    if (!relevantUrl) return;

    const requestCameras = async () => {
      try {
        const camera_commands_list = camerasList.map(({ camera_name, show }) => ({
          camera_name,
          show,
        }));

        console.log("[CamerasGrid] requesting cameras", {
          cartId,
          site: relevantSite,
          camera_commands_list,
        });

        const r = await axios.post(`${relevantUrl}/admin/requestCameras`, {
          camera_commands_list,
          cartId,
          adminId: auth?.userId || null,
        });

        if (r.status !== 200) {
          Swal.fire({ icon: "error", title: "Failed to request cameras", text: r.data });
          setLoading(false);
        } else {
          console.log("[CamerasGrid] requestCameras response", {
            status: r.status,
            data: r.data,
          });
        }
      } catch (err) {
        console.error("[CamerasGrid] requestCameras error", {
          message: err?.message,
          stack: err?.stack,
        });
        Swal.fire({ icon: "error", title: "Error", text: err.message });
        setLoading(false);
      }
    };

    setCameraImages([]);
    setLoading(true);
    console.log("[CamerasGrid] (re)requesting images: reset state & set loading", {
      cartId,
      site: relevantSite,
    });
    requestCameras();
  }, [relevantUrl, cartId, auth]);

  /* ───────── render ───────── */
  return (
    <Box
      sx={{
        width: "100%",
        mb: 4,
        minHeight: 240,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {loading ? (
        <Box sx={{ width: 90, height: 90 }}>
          <CircularProgressbar
            value={spinner}
            strokeWidth={6}
            styles={buildStyles({
              pathColor: "#1976d2",
              trailColor: "#e0e0e0",
              pathTransitionDuration: 0,
              strokeLinecap: "butt",
            })}
          />
        </Box>
      ) : cameraImages.length ? (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexWrap: "nowrap",
            gap: 2,
            p: 2,
            justifyContent: "space-between",
            overflow: "hidden",
          }}
        >
          {cameraImages.map(({ name, image }) => (
            <Box
              key={name}
              sx={{
                flex: "1 1 0",
                textAlign: "center",
                border: "1px solid #ddd",
                borderRadius: 1,
                p: 2,
                bgcolor: "#f9f9f9",
              }}
            >
              <Typography variant="h6" gutterBottom>
                {name[0].toUpperCase() + name.slice(1)}
              </Typography>
              <img
                src={image}
                alt={name}
                style={{ width: "100%", objectFit: "contain", borderRadius: 4 }}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body1" align="center">
          No camera images available
        </Typography>
      )}
    </Box>
  );
}
